import React from 'react';
import { clsx } from 'clsx';
import type { AvatarProps } from '@/types/ui';

/**
 * Webcoda-styled Avatar component
 * Used for user profiles, agent assignments, and customer identification
 */
export const Avatar: React.FC<AvatarProps> = ({
  src,
  alt,
  name,
  size = 'md',
  shape = 'circle',
  fallbackIcon,
  showOnline = false,
  online = false,
  clickable = false,
  onClick,
  border = false,
  loading = false,
  className,
  'data-testid': testId,
  ...props
}) => {
  // Generate initials from name
  const getInitials = (name: string): string => {
    return name
      .split(' ')
      .map(word => word.charAt(0))
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  // Base avatar classes
  const baseClasses = [
    'inline-flex',
    'items-center',
    'justify-center',
    'font-medium',
    'text-gray-700',
    'bg-gray-100',
    'overflow-hidden',
    'select-none',
    'flex-shrink-0',
    'transition-all',
    'duration-200'
  ];

  // Size classes
  const sizeClasses = {
    xs: ['w-6', 'h-6', 'text-xs'],
    sm: ['w-8', 'h-8', 'text-sm'],
    md: ['w-10', 'h-10', 'text-sm'],
    lg: ['w-12', 'h-12', 'text-base'],
    xl: ['w-16', 'h-16', 'text-lg']
  };

  // Shape classes
  const shapeClasses = shape === 'circle' ? ['rounded-full'] : ['rounded-md'];

  // Interaction classes
  const interactionClasses = clickable
    ? ['cursor-pointer', 'hover:opacity-75', 'focus:outline-none', 'focus:ring-2', 'focus:ring-primary-500', 'focus:ring-offset-2']
    : [];

  // Border classes
  const borderClasses = border ? ['ring-2', 'ring-white', 'shadow-sm'] : [];

  // Loading classes
  const loadingClasses = loading ? ['animate-pulse', 'bg-gray-200'] : [];

  const avatarClasses = clsx(
    baseClasses,
    sizeClasses[size],
    shapeClasses,
    interactionClasses,
    borderClasses,
    loadingClasses,
    className
  );

  // Online indicator size based on avatar size
  const onlineIndicatorSize = {
    xs: 'w-1.5 h-1.5',
    sm: 'w-2 h-2',
    md: 'w-2.5 h-2.5', 
    lg: 'w-3 h-3',
    xl: 'w-4 h-4'
  };

  const onlineIndicatorPosition = {
    xs: 'bottom-0 right-0',
    sm: 'bottom-0 right-0',
    md: 'bottom-0.5 right-0.5',
    lg: 'bottom-0.5 right-0.5',
    xl: 'bottom-1 right-1'
  };

  const handleClick = () => {
    if (clickable && onClick) {
      onClick();
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (clickable && (event.key === 'Enter' || event.key === ' ')) {
      event.preventDefault();
      handleClick();
    }
  };

  const renderFallback = () => {
    if (loading) {
      return null; // Show loading state background
    }

    if (fallbackIcon) {
      return fallbackIcon;
    }

    if (name) {
      return (
        <span className="font-semibold">
          {getInitials(name)}
        </span>
      );
    }

    // Default user icon
    return (
      <svg
        className={clsx(
          'text-gray-400',
          size === 'xs' ? 'w-4 h-4' :
          size === 'sm' ? 'w-5 h-5' :
          size === 'md' ? 'w-6 h-6' :
          size === 'lg' ? 'w-7 h-7' :
          'w-8 h-8'
        )}
        fill="currentColor"
        viewBox="0 0 24 24"
        aria-hidden="true"
      >
        <path d="M24 20.993V24H0v-2.996A14.977 14.977 0 0112.004 15c4.904 0 9.26 2.354 11.996 5.993zM16.002 8.999a4 4 0 11-8 0 4 4 0 018 0z" />
      </svg>
    );
  };

  return (
    <div className="relative inline-flex">
      <div
        className={avatarClasses}
        onClick={handleClick}
        onKeyDown={handleKeyDown}
        tabIndex={clickable ? 0 : -1}
        role={clickable ? 'button' : 'img'}
        aria-label={alt || (name ? `Avatar for ${name}` : 'User avatar')}
        data-testid={testId}
        {...props}
      >
        {src ? (
          <img
            src={src}
            alt={alt || name || 'Avatar'}
            className="w-full h-full object-cover"
            onError={(e) => {
              // Hide broken image and show fallback
              (e.target as HTMLImageElement).style.display = 'none';
            }}
          />
        ) : (
          renderFallback()
        )}
      </div>
      
      {/* Online indicator */}
      {showOnline && (
        <span
          className={clsx(
            'absolute',
            'rounded-full',
            'ring-2',
            'ring-white',
            onlineIndicatorSize[size],
            onlineIndicatorPosition[size],
            online ? 'bg-success-500' : 'bg-gray-400'
          )}
          aria-hidden="true"
        />
      )}
    </div>
  );
};

/**
 * Avatar Group - Display multiple avatars in a compact group
 */
export const AvatarGroup: React.FC<{
  avatars: Array<{
    src?: string;
    name?: string;
    alt?: string;
  }>;
  size?: AvatarProps['size'];
  max?: number;
  spacing?: 'tight' | 'normal' | 'loose';
  className?: string;
}> = ({ avatars, size = 'md', max = 4, spacing = 'normal', className }) => {
  const visibleAvatars = avatars.slice(0, max);
  const remainingCount = avatars.length - max;

  const spacingClasses = {
    tight: '-space-x-1',
    normal: '-space-x-2',
    loose: '-space-x-1.5'
  };

  return (
    <div className={clsx('flex', spacingClasses[spacing], className)}>
      {visibleAvatars.map((avatar, index) => (
        <Avatar
          key={index}
          src={avatar.src}
          name={avatar.name}
          alt={avatar.alt}
          size={size}
          border
          className="relative"
        />
      ))}
      
      {remainingCount > 0 && (
        <Avatar
          size={size}
          border
          className="relative bg-gray-200"
        >
          <span className="text-gray-600 font-medium">
            +{remainingCount}
          </span>
        </Avatar>
      )}
    </div>
  );
};

// Attach variants to main Avatar component
Object.assign(Avatar, { Group: AvatarGroup });